import { useListState } from "@/hooks/use-list-state";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSEO } from "@/hooks/use-seo";
import { seoFor } from "@/data/marketing-routes";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import type { RecommendedFurnishing } from "@shared/schema";
import { RECOMMENDED_FURNISHING_ROOM_TYPES, RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS } from "@shared/schema";

const ROOM_TYPE_ALL = "all";

function formatPrice(priceCents: number): string {
  if (priceCents === 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    priceCents / 100,
  );
}

export default function RecommendedFurnishingsPage() {
  const [roomFilter, setRoomFilter] = useListState<string>("recommended-furnishings:room", ROOM_TYPE_ALL);

  useSEO(seoFor("/recommendedfurnishings"));

  const { data: items, isLoading } = useQuery<RecommendedFurnishing[]>({
    queryKey: ["/api/public/recommended-furnishings"],
  });

  const filtered =
    roomFilter === ROOM_TYPE_ALL
      ? (items ?? [])
      : (items ?? []).filter((i: RecommendedFurnishing) => i.roomType === roomFilter);

  return (
    <>
      <PublicHeader />

      <main className="min-h-screen bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand to-brand-hover text-white py-16">
          <div className="container mx-auto px-6 max-w-5xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-page-title">
              Recommended Furnishings
            </h1>
            <p className="text-blue-100 text-lg max-w-2xl" data-testid="text-page-description">
              Our team's curated picks for furnishing co-living bedrooms, bathrooms, kitchens, and
              common spaces. Click any item to shop directly.
            </p>
          </div>
        </section>

        {/* Filters + Table */}
        <section className="container mx-auto px-6 max-w-5xl py-10">
          {/* Filter */}
          <div className="flex items-center gap-3 mb-6" data-testid="filter-controls">
            <span className="text-sm font-medium text-gray-600">Filter by room type:</span>
            <Select
              value={roomFilter}
              onValueChange={setRoomFilter}
              data-testid="select-room-type-filter"
            >
              <SelectTrigger className="w-[180px]" data-testid="trigger-room-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ROOM_TYPE_ALL} data-testid="option-all">
                  All
                </SelectItem>
                {RECOMMENDED_FURNISHING_ROOM_TYPES.map((rt) => (
                  <SelectItem key={rt} value={rt} data-testid={`option-${rt}`}>
                    {RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS[rt]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isLoading && items && (
              <span className="text-sm text-gray-500" data-testid="text-item-count">
                {filtered.length} item{filtered.length === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="space-y-3" data-testid="skeleton-table">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full rounded" />
              ))}
            </div>
          )}

          {/* Empty */}
          {!isLoading && filtered.length === 0 && (
            <div
              className="text-center py-16 text-gray-500"
              data-testid="text-empty-state"
            >
              {roomFilter === ROOM_TYPE_ALL
                ? "No furnishings listed yet. Check back soon!"
                : `No items listed for ${RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS[roomFilter as keyof typeof RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS] ?? roomFilter} yet.`}
            </div>
          )}

          {/* Table */}
          {!isLoading && filtered.length > 0 && (
            <div className="border rounded-lg overflow-hidden" data-testid="table-furnishings">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Room Type</TableHead>
                    <TableHead className="font-semibold">Item Name</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold text-right">Price</TableHead>
                    <TableHead className="font-semibold">Purchase Link</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow
                      key={item.id}
                      className="hover:bg-gray-50"
                      data-testid={`row-furnishing-${item.id}`}
                    >
                      <TableCell>
                        <Badge variant="secondary" className="whitespace-nowrap">
                          {RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS[item.roomType as keyof typeof RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS] ?? item.roomType}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium" data-testid={`text-name-${item.id}`}>
                        {item.name}
                      </TableCell>
                      <TableCell
                        className="text-sm text-gray-600 max-w-xs"
                        data-testid={`text-description-${item.id}`}
                      >
                        {item.description ?? "—"}
                      </TableCell>
                      <TableCell
                        className="text-right font-medium tabular-nums"
                        data-testid={`text-price-${item.id}`}
                      >
                        {formatPrice(item.priceCents)}
                      </TableCell>
                      <TableCell>
                        {(item.affiliateUrl || item.purchaseUrl) ? (
                          <a
                            href={item.affiliateUrl || item.purchaseUrl || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-brand hover:underline text-sm font-medium"
                            data-testid={`link-purchase-${item.id}`}
                            onClick={() => {
                              // Task #1777 — Best-effort click tracking. Uses
                              // sendBeacon so the navigation isn't blocked.
                              try {
                                const url = `/api/public/recommended-furnishings/${item.id}/click`;
                                if (typeof navigator !== "undefined" && navigator.sendBeacon) {
                                  navigator.sendBeacon(url);
                                } else {
                                  fetch(url, { method: "POST", keepalive: true }).catch(() => {});
                                }
                              } catch {
                                // ignore — tracking must never break the click
                              }
                            }}
                          >
                            Shop <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </main>

      <PublicFooter />
    </>
  );
}
